import React from "react";
import { Drawer, Table, Tag } from "antd";
import { HistoryOutlined } from "@ant-design/icons";
import { tablePagination } from "../../../components";

interface TransactionHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedAccount: any;
  transactionHistoryData: any;
  isHistoryLoading: boolean;
  historyPage: number;
  onHistoryPageChange: (page: number) => void;
}

export const TransactionHistoryDrawer: React.FC<
  TransactionHistoryDrawerProps
> = ({
  open,
  onClose,
  selectedAccount,
  transactionHistoryData,
  isHistoryLoading,
  historyPage,
  onHistoryPageChange,
}) => {
  const transactionColumns = [
    {
      title: "Date",
      dataIndex: "transaction_date",
      key: "transaction_date",
      render: (val: string) => (
        <span className="text-xs">
          {new Date(val).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "transaction_type_code",
      key: "transaction_type_code",
      render: (code: string) => {
        const isDeposit = code === "deposit";
        return (
          <Tag
            color={isDeposit ? "success" : "volcano"}
            className="rounded text-xs uppercase font-medium"
          >
            {isDeposit ? "Deposit" : "Withdrawal"}
          </Tag>
        );
      },
    },
    {
      title: "Ref No.",
      dataIndex: "reference_no",
      key: "reference_no",
      render: (val: string) => (
        <span className="text-xs font-mono text-gray-600">{val || "—"}</span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (val: any, rec: any) => {
        const isDeposit = rec.transaction_type_code === "deposit";
        return (
          <span
            className={`font-bold ${isDeposit ? "text-green-600" : "text-red-500"}`}
          >
            {isDeposit ? "+" : "-"}{" "}
            {Number(val).toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </span>
        );
      },
    },
    {
      title: "Running Balance",
      dataIndex: "balance",
      key: "balance",
      render: (val: any) => (
        <span className="font-semibold text-gray-500">
          {Number(val).toLocaleString("en-US", { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Note / Description",
      dataIndex: "note",
      key: "note",
      render: (val: string) => (
        <span className="text-xs text-gray-500 italic">{val || "—"}</span>
      ),
    },
  ];

  return (
    <Drawer
      title={
        <div className="flex items-center gap-2">
          <HistoryOutlined />
          <span>Transaction Logs: {selectedAccount?.account_name}</span>
        </div>
      }
      width={750}
      open={open}
      onClose={onClose}
    >
      {selectedAccount && (
        <div>
          <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg border mb-4">
            <div>
              <span className="text-gray-400 text-xs block">
                Bank Account / Holder
              </span>
              <span className="font-bold text-gray-700 block text-base">
                {selectedAccount.bank_name}
              </span>
              <span className="text-xs text-mono text-gray-500 font-mono">
                {selectedAccount.account_number} ({selectedAccount.account_name}
                )
              </span>
            </div>
            <div className="text-right">
              <span className="text-gray-400 text-xs block">
                Current Cash Book Balance
              </span>
              <span className="text-xl font-extrabold text-green-600 block">
                LKR{" "}
                {Number(selectedAccount.balance || 0).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>

          <Table
            dataSource={
              Array.isArray(transactionHistoryData)
                ? transactionHistoryData
                : (transactionHistoryData as any)?.bank_transactions || []
            }
            columns={transactionColumns}
            rowKey="id"
            loading={isHistoryLoading}
            size="small"
            pagination={tablePagination({
              current: historyPage,
              pageSize: 10,
              total: (transactionHistoryData as any)?.meta?.total || 0,
              onChange: onHistoryPageChange,
              itemName: "transactions",
            })}
          />
        </div>
      )}
    </Drawer>
  );
};
